import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import type { UIFilter as UIFilterType } from '@unifyapps/network/generated/models/uIFilter';
import get from 'lodash/get';
import _get from 'lodash/get';
import { useCallback } from 'react';
import { buildDependencyGraph } from '../../../dependency/utils/buildDependencyGraph';
import { setIn } from '../../../immutable';
import {
  type DynamicAndConditionalPathHandler,
  dedupeDynamicAndConditionalPathsHandler,
  dynamicAndConditionalPathsHandler,
  isConditionalOperator,
} from '../../context/DependencyGraphProvider/handlers';
import { UIFilter } from '../../filter/api/UIFilter';
import { ConditionEvaluatorImpl } from '../../filter/ConditionEvaluatorHelperImpl';
import { isDynamicValue } from '../../utils/dynamicBindings';
import { recursiveUpdateWithSelector } from '../../utils/recursiveUpdate';
import { sanitizeComputedUIFilters, sanitizeFilters } from '../../utils/sanitizeFilters';
import {
  type UseGetNoCodeComputeDataType,
  useGetNoCodeComputedData,
} from '../computeContext/useGetNoCodeComputedData';

const conditionEvaluator = new ConditionEvaluatorImpl();

export function getConditionOperatorEvaluationFunc({
  conditionEvaluationObj,
  context,
  getNoCodeBulkComputedData,
  isPreComputed,
}: {
  conditionEvaluationObj?: ConditionEvaluationObject;
  context: Record<string, unknown>;
  getNoCodeBulkComputedData: UseGetNoCodeComputeDataType['getNoCodeBulkComputedData'];
  // this is introduced because now in block visibility or any conditions of a block is no longer evaluated inside the filter, but it is already pre computed so we do not need to bulk create deps to find the dynamic paths
  isPreComputed?: boolean;
}) {
  if (!conditionEvaluationObj) {
    return undefined;
  }

  if (conditionEvaluationObj.type === 'boolean') {
    return conditionEvaluationObj.payload;
  }

  try {
    // using sanitizeComputedUIFilters on pre computed because we need to handle that for op missing and exists it is possible that property is empty
    const sanitizedFilters = isPreComputed
      ? sanitizeComputedUIFilters(conditionEvaluationObj.payload)
      : sanitizeFilters(conditionEvaluationObj.payload);
    // only consider pre computed if it is true, undefined and false means that the condition evaluation object is not pre computed
    const bulkComputedFilters = isPreComputed
      ? sanitizedFilters
      : bulkComputeFilterPaths(sanitizedFilters, context, getNoCodeBulkComputedData);
    const uiFilter = UIFilter.createUIFilter(bulkComputedFilters as UIFilterType);
    const filter = uiFilter.toFilter();
    return conditionEvaluator.evaluate(filter);
  } catch (e) {
    console.error('Error evaluating condition', e, conditionEvaluationObj.payload);
  }
}

/**
 * Extracts all dynamic paths from a filter object and bulk computes them, this function is needed because right now all the code where filter computation is done expects that the filter will handle evaluation inside and they might not give evaluated values
 */
function bulkComputeFilterPaths(
  filterObject: UIFilterType,
  context: Record<string, unknown>,
  getNoCodeBulkComputedData: UseGetNoCodeComputeDataType['getNoCodeBulkComputedData'],
): unknown {
  if (!filterObject) {
    return filterObject;
  }

  // Extract dynamic paths using buildDependencyGraph with handlers
  const dependencyGraph = buildDependencyGraph<DynamicAndConditionalPathHandler>({
    objects: { filter: filterObject },
    isValidEntity: () => true, // Accept all entities for path extraction
    handlers: [dynamicAndConditionalPathsHandler],
    dedupeHandlers: [dedupeDynamicAndConditionalPathsHandler],
  });

  const filterDependencies = dependencyGraph.filter;
  if (!filterDependencies?.output?.dynPaths || filterDependencies.output.dynPaths.length === 0) {
    return filterObject;
  }

  // Collect string values at dynamic paths for bulk evaluation
  const bulkExpressions: Record<string, string> = {};

  // Extract paths from the dependency graph
  for (const dynPath of filterDependencies.output.dynPaths) {
    const value = _get(filterObject, dynPath.path);
    if (isDynamicValue(value)) {
      bulkExpressions[dynPath.path] = value;
    }
  }

  // Perform bulk evaluation if we have expressions
  if (Object.keys(bulkExpressions).length > 0) {
    const evaluatedResults = getNoCodeBulkComputedData(bulkExpressions, context);

    // Update the filter object with evaluated results
    let updatedFilterObject = { ...filterObject };
    // iterating through bulkExpression because evaluatedResults might not have all the keys we send it to evaluate with
    // if the key resolved to undefined, then it will not be in evaluatedResults
    for (const path of Object.keys(bulkExpressions)) {
      const evaluatedValue = evaluatedResults[path];
      updatedFilterObject = setIn(updatedFilterObject, path, evaluatedValue);
    }

    return updatedFilterObject;
  }

  return filterObject;
}

export type EvaluateConditionalOperator = (params: {
  conditionEvaluationObj: ConditionEvaluationObject | undefined;
  context: Record<string, unknown>;
  isPreComputed?: boolean;
}) => boolean | undefined;

export function useEvaluateConditionalOperator(): {
  evaluateConditionalOperator: EvaluateConditionalOperator;
} {
  const { getNoCodeBulkComputedData } = useGetNoCodeComputedData();

  const evaluateConditionalOperator = useCallback(
    (params: {
      conditionEvaluationObj: ConditionEvaluationObject | undefined;
      context: Record<string, unknown>;
      isPreComputed?: boolean;
    }) => {
      return getConditionOperatorEvaluationFunc({
        ...params,
        getNoCodeBulkComputedData,
      });
    },
    [getNoCodeBulkComputedData],
  );

  return { evaluateConditionalOperator };
}

export type UseEvaluateConditionalOperator = ReturnType<typeof useEvaluateConditionalOperator>;

export function getConditionEvaluationFunc<T>(
  object: Record<string, unknown>,
  context: Record<string, unknown>,
  getNoCodeBulkComputedData: UseGetNoCodeComputeDataType['getNoCodeBulkComputedData'],
  isPreComputed = false,
) {
  return recursiveUpdateWithSelector({
    obj: object,
    updater: (_key, value) => {
      const conditionEvaluationObj = value as ConditionEvaluationObject | undefined;

      return getConditionOperatorEvaluationFunc({
        conditionEvaluationObj,
        context,
        getNoCodeBulkComputedData,
        isPreComputed,
      });
    },
    // valueSelector is a function that takes a key and a value and returns a boolean if the value should be updated
    valueSelector: isConditionalOperator,
  }) as T;
}

export function getConditionEvaluationFuncForSelectedPaths<T>(
  object: Record<string, unknown>,
  paths: string[],
  context: Record<string, unknown>,
  getNoCodeBulkComputedData: UseGetNoCodeComputeDataType['getNoCodeBulkComputedData'],
) {
  let updatedObject = { ...object };
  for (const path of paths) {
    const conditionEvaluationObj = get(updatedObject, path) as
      | ConditionEvaluationObject
      | undefined;

    if (!isConditionalOperator('conditions', conditionEvaluationObj)) continue;

    try {
      const evaluatedValue = getConditionOperatorEvaluationFunc({
        conditionEvaluationObj,
        context,
        getNoCodeBulkComputedData,
        isPreComputed: false,
      });

      updatedObject = setIn(updatedObject, path, evaluatedValue);
    } catch (e) {
      console.error(
        `Error evaluating condition at path: ${path}`,
        e,
        conditionEvaluationObj.payload,
      );
    }
  }

  return updatedObject;
}

/**
 * Given any object, this will traverse the object and figure out if there is an structure of some keys which matches the conditional evaluator, if yes it will evaluate the conditional operator
 */
export function useComputeConditionEvaluationGetter() {
  const { getNoCodeBulkComputedData } = useGetNoCodeComputedData();

  return useCallback(
    <T>(
      object: Record<string, unknown>,
      context: Record<string, unknown>,
      isPreComputed?: boolean,
    ) => {
      return getConditionEvaluationFunc<T>(
        object,
        context,
        getNoCodeBulkComputedData,
        isPreComputed,
      );
    },
    [getNoCodeBulkComputedData],
  );
}

/**
 * Given any object, and a list of paths, will iterate through the paths and evaluate the conditional operator for each path
 * and return a new object with the evaluated values.
 */
export function useComputeConditionEvaluationGetterForSelectedPaths() {
  const { getNoCodeBulkComputedData } = useGetNoCodeComputedData();

  return useCallback(
    <T>(object: Record<string, unknown>, paths: string[], context: Record<string, unknown>) => {
      return getConditionEvaluationFuncForSelectedPaths<T>(
        object,
        paths,
        context,
        getNoCodeBulkComputedData,
      );
    },
    [getNoCodeBulkComputedData],
  );
}
